import React, { useEffect, useState } from "react";
import { Field, FieldProps, getIn } from "formik";
import { InputGroup } from "@blueprintjs/core";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { fields } from "../models/settings";
import { REMINDER_MAX_DAYS } from "../validation";
import optionStyles from "./WorkloadPlanSettings.module.scss";
import styles from "./DeadlineRemindersSettings.module.scss";

/** Поля карточки: у писем есть только часть из них */
interface ReminderValue {
    enabled: boolean;
    daysBefore: number[];
    workingDays: boolean;
    sendTime: string;
    onDeadlineDay?: boolean;
    overdue?: boolean;
    overdueDays?: number;
    noImplementer?: boolean;
}

type ReminderField = keyof ReminderValue;

interface CardProps {
    value: ReminderValue;
    path: (name: ReminderField) => string;
    error: (name: ReminderField) => string | undefined;
    set: (name: ReminderField, value: any) => void;
}

/** «3, 1» → [3, 1]; нечисловое превращается в 0, чтобы проверка формы его показала */
const parseDays = (text: string) =>
    text
        .split(/[\s,;]+/)
        .filter((part) => part.length > 0)
        .map((part) => (/^\d+$/.test(part) ? parseInt(part, 10) : 0));

const formatDays = (days: number[]) => days.join(", ");

/**
 * Все уведомления о сроках: по карточке на вид срока. Внутри карточки — что слать
 * (заранее, в день срока, о просрочке…) и во сколько
 */
export const DeadlineRemindersSettings: React.FC = () => (
    <div className={styles.cards}>
        <ReminderCard kind="questions" render={(props) => <QuestionsCard {...props} />} />
        <ReminderCard kind="mails" render={(props) => <MailsCard {...props} />} />
    </div>
);

const ReminderCard: React.FC<{ kind: "questions" | "mails"; render: (props: CardProps) => React.ReactNode }> = ({
    kind,
    render,
}) => (
    <Field name={`${fields.deadlineReminders}.${kind}`}>
        {({ field, form }: FieldProps) => {
            const path = (name: ReminderField) => `${field.name}.${name}`;
            const props: CardProps = {
                value: field.value,
                path,
                error: (name) => getIn(form.errors, path(name)),
                set: (name, next) => {
                    form.setFieldValue(path(name), next);
                    form.setFieldTouched(path(name), true, false);
                },
            };

            return <>{render(props)}</>;
        }}
    </Field>
);

interface KindTexts {
    title: string;
    description: string;
    onDeadlineDay: string;
    overdue: string;
    sendTime: string;
}

const QUESTIONS: KindTexts = {
    title: "Задания",
    description: "Исполнителям и ответственным за задание, пока оно не выполнено.",
    onDeadlineDay: "«Сегодня последний срок выполнения»",
    overdue: "«Просрочено» — каждый рабочий день после срока",
    sendTime: "Все уведомления по заданиям приходят в это время, только в рабочие дни.",
};

const MAILS: KindTexts = {
    title: "Письма корреспонденции",
    description: "Исполнителю и ответственному за письмо, пока оно не исполнено.",
    onDeadlineDay: "«Сегодня последний срок ответа»",
    overdue: "«Просрочено: срок ответа был …» — каждый рабочий день после срока",
    sendTime: "Все уведомления по письмам приходят в это время, только в рабочие дни.",
};

const QuestionsCard: React.FC<CardProps> = (props) => (
    <DeadlineCard {...props} texts={QUESTIONS}>
        <Toggle
            checked={!!props.value.noImplementer}
            label="Если не назначен исполнитель"
            hint="Ответственному — «необходимо назначить Исполнителя»"
            onChange={(checked) => props.set("noImplementer", checked)}
        />
    </DeadlineCard>
);

const MailsCard: React.FC<CardProps> = (props) => <DeadlineCard {...props} texts={MAILS} />;

/** Карточка вида срока: заранее, в день срока, о просрочке, своё (children) и время отправки */
const DeadlineCard: React.FC<CardProps & { texts: KindTexts; children?: React.ReactNode }> = (props) => {
    const { value, set, path, error, texts, children } = props;
    const active = value.enabled || value.onDeadlineDay || value.overdue || value.noImplementer;

    return (
        <Card title={texts.title} description={texts.description} active={!!active}>
            <AdvanceRow {...props} />

            <Toggle
                checked={true}
                locked={true}
                label="В день срока"
                hint={`${texts.onDeadlineDay} — приходит всегда`}
            />

            <Toggle
                checked={!!value.overdue}
                label="О просрочке"
                hint={texts.overdue}
                onChange={(checked) => set("overdue", checked)}
            >
                <div className={styles.inline}>
                    <span>не дольше</span>
                    <InputGroup
                        id={path("overdueDays")}
                        className={`planr-default-input ${styles.number}`}
                        value={
                            value.overdueDays !== undefined && value.overdueDays >= 0 ? String(value.overdueDays) : ""
                        }
                        autoComplete="off"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const text = e.target.value.trim();
                            // пустое и нечисловое — -1: проверка формы подскажет допустимый диапазон
                            set("overdueDays", /^\d+$/.test(text) ? parseInt(text, 10) : -1);
                        }}
                    />
                    <span>
                        {value.overdueDays === 0
                            ? "дней после срока — без ограничения, пока не закрыто"
                            : `${dayWord(value.overdueDays || 0)} после срока, дальше не напоминать`}
                    </span>
                </div>
                <div className={styles.hint}>0 — напоминать без ограничения</div>
                <Error text={error("overdueDays")} />
            </Toggle>

            {children}

            <SendTime {...props} hint={texts.sendTime} />
        </Card>
    );
};

/** Напоминание заранее: за сколько дней и как их считать */
const AdvanceRow: React.FC<CardProps> = ({ value, set, path, error }) => (
    <Toggle
        checked={value.enabled}
        label="Заранее, до срока"
        hint={describe(value)}
        onChange={(checked) => set("enabled", checked)}
    >
        <DaysInput
            id={path("daysBefore")}
            days={value.daysBefore}
            error={error("daysBefore")}
            onChange={(days) => set("daysBefore", days)}
        />

        <div className={styles.check} onClick={() => set("workingDays", !value.workingDays)}>
            <PlanrButton
                type="neutral"
                icon={value.workingDays ? "general-checkbox" : "general-zero-checkbox"}
                size="small"
            />
            <span>Считать рабочие дни по производственному календарю</span>
        </div>
    </Toggle>
);

const SendTime: React.FC<CardProps & { hint: string }> = ({ value, set, path, error, hint }) => (
    <div className={styles.footer}>
        <label className={optionStyles.subtitle} htmlFor={path("sendTime")}>
            Время отправки
        </label>
        <div className={styles.inline}>
            <InputGroup
                id={path("sendTime")}
                type="time"
                className={`planr-default-input ${styles.time}`}
                value={value.sendTime}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("sendTime", e.target.value)}
            />
            <span className={optionStyles.description}>{hint}</span>
        </div>
        <Error text={error("sendTime")} />
    </div>
);

const Card: React.FC<{ title: string; description: string; active: boolean; children?: React.ReactNode }> = ({
    title,
    description,
    active,
    children,
}) => (
    <div className={`${styles.card} ${active ? styles.enabled : ""}`}>
        <div className={styles.header}>
            <div className={optionStyles.label}>{title}</div>
            <div className={optionStyles.description}>{description}</div>
        </div>
        <div className={styles.body}>{children}</div>
    </div>
);

/** Строка-переключатель; уточняющие поля показываются под ней, только когда она включена */
const Toggle: React.FC<{
    checked: boolean;
    label: string;
    hint?: string;
    /** Включено всегда: флажок показывается, но не переключается */
    locked?: boolean;
    onChange?: (checked: boolean) => void;
    children?: React.ReactNode;
}> = ({ checked, label, hint, locked, onChange, children }) => (
    <div className={styles.row}>
        <div
            className={`${styles.toggle} ${locked ? styles.locked : ""}`}
            onClick={() => !locked && onChange?.(!checked)}
        >
            <PlanrButton type="neutral" icon={checked ? "general-checkbox" : "general-zero-checkbox"} size="small" />
            <div className={optionStyles.text}>
                <div className={styles.rowLabel}>{label}</div>
                {hint && <div className={optionStyles.description}>{hint}</div>}
            </div>
        </div>
        {checked && children && <div className={styles.nested}>{children}</div>}
    </div>
);

/**
 * Поле «за сколько дней»: держит набранный текст, пока его редактируют,
 * а в форму отдаёт уже разобранные числа
 */
const DaysInput: React.FC<{ id: string; days: number[]; error?: string; onChange: (days: number[]) => void }> = ({
    id,
    days,
    error,
    onChange,
}) => {
    const [text, setText] = useState(formatDays(days));

    // значение поменялось снаружи (загрузка, сохранение, сброс формы) — показываем его
    useEffect(() => {
        setText((current) => (formatDays(parseDays(current)) === formatDays(days) ? current : formatDays(days)));
    }, [days]);

    return (
        <div>
            <div className={styles.inline}>
                <span>за</span>
                <InputGroup
                    id={id}
                    className={`planr-default-input ${styles.days}`}
                    placeholder="3, 1"
                    value={text}
                    autoComplete="off"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setText(e.target.value);
                        onChange(parseDays(e.target.value));
                    }}
                />
                <span>дн. до срока, через запятую</span>
            </div>
            <Error text={error} />
        </div>
    );
};

const Error: React.FC<{ text?: string }> = ({ text }) => (text ? <div className={styles.error}>{text}</div> : null);

/** Пример словами: «За 3 и за 1 рабочий день до срока» */
function describe(value: ReminderValue) {
    const days = [...value.daysBefore].filter((d) => d >= 1 && d <= REMINDER_MAX_DAYS).sort((a, b) => b - a);

    if (days.length === 0) {
        return "Укажите, за сколько дней напоминать";
    }

    const unit = (d: number) => (value.workingDays ? `${workingWord(d)} ${dayWord(d)}` : dayWord(d));
    const list = days.map((d, i) => (i === days.length - 1 ? `за ${d} ${unit(d)}` : `за ${d}`)).join(" и ");

    return `${list.charAt(0).toUpperCase()}${list.slice(1)} до срока`;
}

function dayWord(n: number) {
    const tens = n % 100;
    const units = n % 10;

    if (tens >= 11 && tens <= 14) {
        return "дней";
    }

    return units === 1 ? "день" : units >= 2 && units <= 4 ? "дня" : "дней";
}

function workingWord(n: number) {
    const tens = n % 100;
    return n % 10 === 1 && tens !== 11 ? "рабочий" : "рабочих";
}
